import React, { createContext, useContext, useState, useCallback } from 'react';

export type PleriRenderType = 'sidebar' | 'navbar' | 'extension' | 'custom';
export type PleriState = 'closed' | 'mini' | 'fullscreen';

interface PleriRenderContextType {
  // State
  isOpen: boolean;
  pleriState: PleriState;
  renderType: PleriRenderType;

  // Actions
  open: (state?: PleriState) => void;
  close: () => void;
  setPleriState: (state: PleriState) => void;
  setRenderType: (type: PleriRenderType) => void;
  toggle: () => void;
}

export const PleriRenderContext = createContext<
  PleriRenderContextType | undefined
>(undefined);

export const usePleriRender = () => {
  const context = useContext(PleriRenderContext);
  if (!context) {
    throw new Error('usePleriRender must be used within PleriRenderProvider');
  }
  return context;
};

interface PleriRenderProviderProps {
  children: React.ReactNode;
  defaultRenderType?: PleriRenderType;
  defaultState?: PleriState;
}

export const PleriRenderProvider: React.FC<PleriRenderProviderProps> = ({
  children,
  defaultRenderType = 'sidebar',
  defaultState = 'closed',
}) => {
  const [pleriState, setPleriStateInternal] =
    useState<PleriState>(defaultState);
  const [renderType, setRenderTypeInternal] =
    useState<PleriRenderType>(defaultRenderType);

  const isOpen = pleriState !== 'closed';

  const open = useCallback((state: PleriState = 'mini') => {
    window.dispatchEvent(
      new CustomEvent<PleriState>('pleri-render-change', { detail: state }),
    );

    setPleriStateInternal(state);
  }, []);

  const close = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent<PleriState>('pleri-render-change', { detail: 'closed' }),
    );
    setPleriStateInternal('closed');
  }, []);

  const setPleriState = useCallback((state: PleriState) => {
    window.dispatchEvent(
      new CustomEvent<PleriState>('pleri-render-change', { detail: state }),
    );
    setPleriStateInternal(state);
  }, []);

  const setRenderType = useCallback((type: PleriRenderType) => {
    setRenderTypeInternal(type);
    // Save to localStorage for persistence
    localStorage.setItem('pleriRenderType', type);
  }, []);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, close, open]);

  // Load saved renderType from localStorage on mount
  React.useEffect(() => {
    const savedType = localStorage.getItem(
      'pleriRenderType',
    ) as PleriRenderType;
    if (savedType && ['sidebar', 'navbar', 'custom'].includes(savedType)) {
      setRenderTypeInternal(savedType);
    }
  }, []);

  const value: PleriRenderContextType = {
    isOpen,
    pleriState,
    renderType,
    open,
    close,
    setPleriState,
    setRenderType,
    toggle,
  };

  return (
    <PleriRenderContext.Provider value={value}>
      {children}
    </PleriRenderContext.Provider>
  );
};
